import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { usePerson } from '../contexts/PersonContext'
import { useToast } from '../contexts/ToastContext'
import { groups } from '../lib/api'
import { useBrowse } from '../hooks/useBrowse'
import { useActionBoundary } from '../hooks/useActionBoundary'
import { Card } from '../components/ui/Card'
import { Pill } from '../components/ui/Pill'
import { EmptyState } from '../components/ui/EmptyState'
import { cn } from '../lib/cn'
import type { BrowseParams, BrowseResultDto } from '../types/community'

const GAME_PILLS = [
  { value: '', label: 'All games' },
  { value: 'minecraft', label: 'Minecraft' },
  { value: 'wobbly-life', label: 'Wobbly Life' },
  { value: 'mecha-chameleon', label: 'Mecha Chameleon' },
]

const STYLE_PILLS = [
  { value: '', label: 'Any play style' },
  { value: 'Competitive', label: '1v1 · competitive' },
  { value: 'Calm', label: 'Calm · creative' },
  { value: 'Silly', label: 'Silly · co-op' },
]

const SORTS: Array<{ value: NonNullable<BrowseParams['sort']>; label: string }> = [
  { value: 'active', label: 'Most active' },
  { value: 'kind', label: 'Kindness score' },
  { value: 'small', label: 'Smallest first' },
  { value: 'new', label: 'Newest' },
  { value: 'age', label: 'Closest to my age' },
]

export function BrowsePage() {
  const { role, guardianFirst, activePerson } = usePerson()
  const { flash } = useToast()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const beginAction = useActionBoundary('browse')

  const [query, setQuery] = useState('')
  const [game, setGame] = useState('')
  const [style, setStyle] = useState('')
  const [sort, setSort] = useState<NonNullable<BrowseParams['sort']>>('active')
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState('')
  const inFlight = useRef(false)
  useEffect(() => { inFlight.current = false; setPendingId(null); setActionError('') }, [activePerson?.id])

  const params: BrowseParams = {
    query: query || undefined,
    game: game || undefined,
    style: style || undefined,
    sort,
  }
  const browse = useBrowse(params)
  const { data: results = [], isLoading, isError } = browse

  const sortLabel = SORTS.find((s) => s.value === sort)?.label ?? 'Most active'
  const invalidateBrowse = () => {
    for (const key of ['browse', 'groups', 'approvals', 'me']) void qc.invalidateQueries({ queryKey: ['community', key] })
  }

  function clearFilters() {
    setQuery('')
    setGame('')
    setStyle('')
    setSort('active')
  }

  function ctaLabel(g: BrowseResultDto): string {
    if (pendingId === g.id) return 'Sending request…'
    if (g.state === 'joined') return 'Open group'
    if (g.state === 'requested') return 'Requested'
    if (role === 'parent') return 'View group'
    return 'Ask to join'
  }

  async function onCta(g: BrowseResultDto) {
    if (inFlight.current) return
    if (g.state === 'joined') {
      navigate(`/groups/${g.id}`)
      return
    }
    if (g.state === 'requested') {
      flash(`You have already asked to join ${g.name} — the host will decide.`)
      return
    }

    if (role === 'parent') {
      navigate(`/groups/${g.id}`)
      return
    }

    const isCurrent = beginAction()
    inFlight.current = true
    setPendingId(g.id)
    setActionError('')
    try {
      await groups.askToJoinGroup(g.id)
      if (!isCurrent()) return
      flash(`Sent to ${guardianFirst || 'your grown-up'}, then the host.`)
      invalidateBrowse()
    } catch (err) {
      if (isCurrent()) setActionError((err as Error)?.message ?? 'That request did not go through. Please try again.')
    } finally {
      if (isCurrent()) { inFlight.current = false; setPendingId(null) }
    }
  }

  return (
    <div className="min-w-0 animate-rise px-4 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-faint">Browse groups</div>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">
        Find a group to play in
      </h1>
      <p className="mt-3 max-w-[60ch] text-[15px] leading-relaxed text-ink-muted">
        Groups are per-game, and you join as yourself. Find one that fits — you can invite a circle
        so people you already trust can find you there, but joining is always each person's own choice.
      </p>

      {/* Search + sort */}
      <div className="mt-6 flex flex-wrap items-end gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search groups, games, play styles…"
          aria-label="Search groups"
          className="min-w-0 basis-full flex-1 rounded-input border border-line bg-paper-surface px-[14px] py-3 text-[14px] text-ink outline-none placeholder:text-ink-fainter focus:border-terra sm:basis-auto"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as NonNullable<BrowseParams['sort']>)}
          aria-label="Sort groups"
          className="max-w-full rounded-input border border-line bg-paper-surface px-[14px] py-3 text-[14px] text-ink outline-none focus:border-terra"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Game pills */}
      <div className="mt-4 flex flex-wrap gap-2">
        {GAME_PILLS.map((g) => (
          <button
            key={g.value || 'all'}
            type="button"
            onClick={() => setGame(g.value)}
            aria-pressed={game === g.value}
            className={cn(
              'rounded-pill border px-[14px] py-2 text-[13px] transition-colors',
              game === g.value ? 'border-terra bg-terra text-paper-surface' : 'border-line bg-paper-surface text-ink-muted hover:border-terra',
            )}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Style pills (dashed) */}
      <div className="mt-2 flex flex-wrap gap-2">
        {STYLE_PILLS.map((s) => (
          <button
            key={s.value || 'any'}
            type="button"
            onClick={() => setStyle(s.value)}
            aria-pressed={style === s.value}
            className={cn(
              'rounded-pill border border-dashed px-[14px] py-2 text-[13px] transition-colors',
              style === s.value ? 'border-terra text-terra-deep' : 'border-line text-ink-muted hover:border-terra',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Count + clear */}
      <div className="mt-5 flex items-center justify-between gap-3">
        <div className="font-mono text-[11px] uppercase tracking-label text-ink-faint">
          {isError ? 'Group results unavailable' : `${results.length} group${results.length === 1 ? '' : 's'}`} · sorted by {sortLabel}
        </div>
        <button type="button" onClick={clearFilters} className="text-[12px] text-terra-deep hover:text-terra-hover">
          Clear filters
        </button>
      </div>

      {/* Results */}
      <div className="mt-4">
        {actionError && <div role="alert" className="mb-3 text-[13px] text-terra-deep">{actionError}</div>}
        {isLoading ? (
          <p className="font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
        ) : isError ? (
          <div role="alert" className="rounded-card border border-line bg-paper-surface p-5">
            <p className="text-[14px] text-ink-muted">Groups could not be loaded. Your filters are saved. Please try again.</p>
            <button type="button" disabled={browse.isFetching} onClick={() => void browse.refetch()} className="mt-3 rounded-btn bg-terra px-4 py-2 text-[13px] text-paper-surface disabled:opacity-50">Retry loading groups</button>
          </div>
        ) : results.length === 0 ? (
          <EmptyState
            dashed
            description="Nothing matches that yet. Try a different game, or start your own circle with the + in the sidebar."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {results.map((g) => (
              <Card key={g.id} padding="none" className="overflow-hidden">
                <div className="h-[5px] w-full" style={{ background: g.tint }} />
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 break-words">
                      <div className="text-[15px] font-semibold text-ink">{g.name}</div>
                      <div className="text-[12px] text-ink-muted">{g.meta}</div>
                    </div>
                    <Pill tone={g.access === 'Open' ? 'moss' : 'honey'}>
                      {g.access === 'Open' ? 'OPEN' : 'VOUCH NEEDED'}
                    </Pill>
                  </div>
                  {g.blurb && <p className="mt-3 text-[13px] leading-relaxed text-ink-muted">{g.blurb}</p>}
                  {g.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {g.tags.map((t) => (
                        <Pill key={t}>{t}</Pill>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 font-mono text-[11px] text-ink-faint">
                    {g.memberCount} member{g.memberCount === 1 ? '' : 's'} · ages {g.agesText} ·{' '}
                    <span className="text-moss">kindness {g.kindness.toFixed(1)}</span>
                  </div>
                  <div className="mt-1 text-[12px] text-ink-faint">{g.moderation}</div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => onCta(g)}
                      disabled={pendingId !== null}
                      className={cn(
                        'rounded-btn px-[16px] py-[10px] text-[13px] font-medium transition-colors disabled:opacity-50',
                        g.state === 'joined'
                          ? 'bg-moss-bg text-moss'
                          : g.state === 'requested'
                            ? 'bg-honey-chip text-honey-fg'
                            : 'bg-terra text-paper-surface hover:bg-terra-deep',
                      )}
                    >
                      {ctaLabel(g)}
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
